<template>
    <footer class="col-lg-9 mx-auto pt-2 m-5 d-flex flex-column align-items-center justify-content-center" :class="additionalFooterClasses">
        <nav v-if="config.socialMediaVisible" class="social mt-3" aria-label="breadcrumb" style="--bs-breadcrumb-divider: '';">
            <ol class="breadcrumb breadcrumb-social d-flex justify-content-center">
                <li v-for="socialMediaEntity, i in socialMediaEntities" :key="i" class="breadcrumb-item fs-3 px-3">
                    <a :href="socialMediaEntity.src" target="_blank" data-show-count="false" aria-label="{{ socialMediaEntity.title }}">
                        <i class="bi text-secondary" :class="socialMediaEntity.icon"></i>
                        <span class="visually-hidden">{{ socialMediaEntity.title }}</span>
                    </a>
                </li>
            </ol>
        </nav>
        <nav v-else class="social mt-3 mb-4" aria-label="breadcrumb" style="--bs-breadcrumb-divider: '';">
        </nav>

        <div class="text-center black-50 my-2 fs-6 disclaimer px-3">
            <p v-if="pageSpecificText" v-for="(textItem, i) in pageSpecificText" :key="i" class="fw-bold">{{ textItem }}</p>
            <p v-for="(textItem, i) in config.text" :key="i">{{ textItem }}</p>
        </div>

        <nav aria-label="breadcrumb" style="--bs-breadcrumb-divider: '|';">
            <ol class="breadcrumb py-3">
                <li v-if="config.aboutUs.visible" class="breadcrumb-item"><a :href="config.aboutUs.href" target="_blank">About Us</a></li>
                <li v-if="config.drawdownCostComparerVisible" class="breadcrumb-item"><router-link to="/drawdowncostcomparer" class="breadcrumb-item">Drawdown Charges Comparison</router-link></li>
                <li v-if="config.privacyPolicy.visible" class="breadcrumb-item"><a :href="config.privacyPolicy.href" target="_blank">Privacy Policy</a></li>
                <li v-if="config.cookiePolicy.visible" class="breadcrumb-item"><a :href="config.cookiePolicy.href" target="_blank">Cookie Policy</a></li>
                <li v-if="config.cookieSettings.visible" class="breadcrumb-item"><a @click.prevent="onCookieSettingsClick" target="_self" class="link-button" role="button">Cookie Settings</a></li>
                <li v-if="config.termsAndConditions.visible" class="breadcrumb-item"><a :href="config.termsAndConditions.href" target="_blank">Terms and Conditions</a></li>
                <li v-if="config.contactUs.visible" class="breadcrumb-item"><a :href="config.contactUs.href">Contact Us</a></li>
            </ol>
        </nav>

        <p v-if="config.logoVisible" class="text-center mt-2"><img src="/content/images/guiide-logo.svg" alt="Guiide logo" width="80" /></p>
        <p v-if="config.copyrightVisible" class="text-center black-50 copyright mb-4">&copy; 2013-{{ currentYear }} Bespoke Retirements Limited</p>
    </footer>
</template>

<script setup lang="ts">
    import { onMounted, watch, ref, computed } from "vue";
    import LinkedTitleItem from '@/guiide/ts/linkedTitleItem';
    import { useStore } from "vuex";
    import { disabledConfig, FooterConfiguration } from "@/guiide/ts/configuration/footerConfiguration";
    import { Configuration } from "@/guiide/ts/configuration/configuration";
    import View from "@/guiide/ts/view";

    const props = defineProps({
        paddingTop: { type: Number, default: 5 },
        marginTop: { type: Number, default: 5 }
    });

    const store = useStore();
    const config = ref<FooterConfiguration>(disabledConfig);
    const additionalFooterClasses = ref<string>(`pt-${props.paddingTop} mt-${props.marginTop}`);

    const currentYear = new Date().getFullYear();

    const pageSpecificText = computed(() => {
        const view = store.state.configuration.activeView as View;
        const key = View[view].toLowerCase();
        return config.value.pageSpecificText?.[key] ?? undefined;
    });

    onMounted(() => {
        if (store?.state?.configuration?.footer) {
            config.value = store.state.configuration.footer;
        }
    })

    watch(
        () => store.state.configuration,
        (newConfig: Configuration) => {
            if (newConfig) {
                config.value = newConfig.footer;
            }
        }
    )

    function onCookieSettingsClick() {
        const handler = resolvePath(window, config.value.cookieSettings.href);
    
        if (handler && typeof handler === 'function') {
            handler();
        }
    }

    function resolvePath(root: any, path: string): unknown {
      return path.split('.').reduce((obj, key) => obj?.[key], root);
    }

    const socialMediaEntities: Array<LinkedTitleItem> = [
        new LinkedTitleItem('X', '', 'bi-twitter-x', 'https://twitter.com/guiidepensions?ref_src=twsrc%5Etfw'),
        new LinkedTitleItem('Facebook', '', 'bi-facebook', 'https://www.facebook.com/guiide.co.uk'),
        new LinkedTitleItem('Linkedin', '', 'bi-linkedin', 'https://www.linkedin.com/company/guiide')
    ];
</script>

<style>
    nav.social {
        border-top: 1px solid #ccc;
        width: 100%;
    }

    .social .breadcrumb {
        margin-top: -19px !important;
        text-align: center;
        width: 100%;
    }

    .breadcrumb-social > .breadcrumb-item {
        background-color: #f0f2f4;
    }

    .breadcrumb-item > a {
        color: #04589B;
    }

    .disclaimer {
        font-size: 0.75rem !important;
    }

    a.link-button {
      color: #04589B;
      text-decoration: underline;
      cursor: pointer;
    }

    a.link-button:visited {
      color: #04589B;
    }

    a.link-button:focus {
      outline: 2px solid #04589B;
      outline-offset: 2px;
    }
</style>
